/*
  Pick-a-day delivery (MGP Logistics), behind Theme settings > Delivery rates
  > "Let shoppers choose their delivery day". layout/theme.liquid loads this
  file and pick-a-day.css only when that setting is on, so with it off none
  of this is on the page.

  Worker: the /mgp routes of the delivery rates worker (repo
  waltons-delivery-worker, src/mgp.js). Its origin comes from
  theme.settings.pickADay (snippets/js-variables.liquid): the pick-a-day
  worker URL, or the Delivery rates one when that is blank.
    POST /mgp/availability { postcode, items }  status and earliest day
    POST /mgp/dates        { postcode, items }  the days, plus an enquiry
                                                (enqref) MGP holds for a while
    POST /mgp/reserve      { enqref, date }     holds the chosen day; 409 when
                                                the day has gone
  Bodies go as text/plain (no Content-Type header), a CORS simple request,
  so no preflight. The worker parses the text whatever the type.

  Three places use it:
  1. Product page: delivery-check.js adds one line under the postcode result
     (availability + productLine), or for CUSTOMER_CONTACT a "From" day on
     the Standard row (productFrom). Informational, one SKU.
  2. Configurator: the Delivery day step (snippets/pick-a-day-step.liquid),
     driven by DayStep below. product-configurator.js awaits commitStep
     before /cart/add, so the drawer opens already showing the day.
  3. Basket: <pick-a-day-basket> in the cart drawer and on /cart
     (snippets/pick-a-day-basket.liquid).

  One day per basket: every dates call sends the whole basket plus the
  build, merged by SKU. The storefront never books. It looks up, holds and
  saves the choice as cart attributes, the hand-off to the checkout
  extension and the order flow:
    "Delivery day"   Thursday 8th October 2026, for people
    _mgp_date        2026-10-08
    _mgp_enqref      the MGP enquiry the day is held on
    _mgp_held_until  unix seconds, as a string
    _mgp_postcode    NG24 1AB
    _mgp_basket      sorted SKU:qty signature of what was sent
    _mgp_status      AVAILABLE (only ever saved with a day), or
                     DIRECT_DELIVERY, CUSTOMER_CONTACT or NOT_AVAILABLE (no day;
                     an AVAILABLE answer with no day to choose is saved as
                     CUSTOMER_CONTACT)
  Nothing here stops adding to basket or checking out. Any failure falls
  back to "our team will contact you".
*/
(() => {
  if (window.WaltonsPickADay) return;

  const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/;
  const STORAGE_KEY = 'waltons:delivery-postcode';
  // The worker's own SKU rule (mgp.js). One bad SKU fails the whole request,
  // so lines are filtered here first.
  const SKU = /^[A-Z0-9][A-Z0-9._-]{1,48}$/i;
  const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
  const STATUSES = ['AVAILABLE', 'NOT_AVAILABLE', 'DIRECT_DELIVERY', 'CUSTOMER_CONTACT'];
  const MAX_ITEMS = 20;
  const MAX_QUANTITY = 50;
  // The worker gives MGP 8 seconds.
  const WORKER_TIMEOUT_MS = 9000;
  const SAVE_TIMEOUT_MS = 5000;
  const COMMIT_WAIT_MS = 5000;
  const CHECKOUT_WAIT_MS = 4000;
  const CHOOSE_DEBOUNCE_MS = 300;
  const OPEN_DEBOUNCE_MS = 600;
  const HOLD_MARGIN_S = 120;
  const DEFAULT_HOLD_S = 15 * 60;
  // The longest this page trusts an enquiry for, whatever the clocks say.
  const MAX_HOLD_S = 30 * 60;
  const AVAILABILITY_TTL_MS = 30 * 60 * 1000;
  const ANSWER_TTL_MS = 5 * 60 * 1000;
  // A radio ticked this soon after an arrow key was reached with the keyboard.
  const ARROW_WINDOW_MS = 100;
  // How soon after a rebuild took focus the new calendar takes it back.
  const REFOCUS_MS = 5000;
  const ARROW_KEYS = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Up', 'Down', 'Left', 'Right'];
  // Live region text is written this long after the visual change.
  const ANNOUNCE_DELAY_MS = 150;
  const CLOCK_KEY = 'waltons:pad-clock';

  const KEYS = {
    day: 'Delivery day',
    date: '_mgp_date',
    enqref: '_mgp_enqref',
    heldUntil: '_mgp_held_until',
    postcode: '_mgp_postcode',
    basket: '_mgp_basket',
    status: '_mgp_status',
  };
  const DATE_KEYS = [KEYS.day, KEYS.date, KEYS.enqref, KEYS.heldUntil];
  // What goes when a day is withdrawn: AVAILABLE never stays without one.
  const DAY_KEYS = DATE_KEYS.concat([KEYS.status]);
  const ALL_KEYS = Object.keys(KEYS).map((name) => KEYS[name]);
  const CONTACT_STATUSES = ['DIRECT_DELIVERY', 'CUSTOMER_CONTACT', 'NOT_AVAILABLE'];

  // Customer copy: British English, no em dashes, ordinal dates.
  const COPY = {
    finding: (postcode) => `Finding delivery days for ${postcode}…`,
    choose: (postcode) => `Choose a day for delivery to ${postcode}.`,
    needPostcode: 'Enter your full postcode to see delivery days.',
    badPostcode: 'Please enter a full UK postcode, for example NG24 1AB.',
    holding: (day) => `Holding ${day}…`,
    held: (day, until) => (until ? `${day} is held for you until ${until}.` : `Held for you: ${day}.`),
    taken: 'That day has just gone. Please choose another.',
    changed: 'Your choices changed. Check your delivery day again.',
    changedFlat: 'Your choices changed. Press Show days to choose your delivery day again.',
    showDays: 'Press Show days to see delivery days for your choices.',
    basketChanged: 'Your basket has changed, so please choose your delivery day again.',
    dayGone: 'Your delivery day is no longer available, so please choose another.',
    contact: 'Our team will contact you after you order to arrange your delivery day.',
    stepError: 'We could not load delivery days just now. You can still add to basket and choose your day there.',
    stepHoldError: 'We could not hold that day just now. You can still add to basket and choose your day there.',
    basketError: 'We could not load delivery days just now. Please try again, or our team will contact you after you order to arrange your day.',
    holdError: 'We could not hold that day just now. Please try again, or our team will contact you after you order to arrange your day.',
    saveError: 'We could not save your day just now. Please choose it again.',
    footerDay: 'Delivery day:',
    footerChange: 'Change',
    footerChangeLabel: 'Change your delivery day',
    footerChoose: 'Choose your delivery day',
  };

  const now = () => Date.now();
  const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

  /* ---------- Clock ---------- */

  // MGP's hold times are absolute, so a phone whose clock is off would
  // misjudge them. The shop's clock comes from the Date header of our own
  // same-origin cart calls (the worker's is not exposed cross-origin) and
  // is kept for the session. Until one has been seen, the device clock.
  let clockOffset = 0;
  try {
    clockOffset = parseInt(sessionStorage.getItem(CLOCK_KEY), 10) || 0;
  } catch (e) { /* storage blocked */ }

  function learnClock(response) {
    try {
      const header = response && response.headers && typeof response.headers.get === 'function' ? response.headers.get('Date') : '';
      const server = header ? Date.parse(header) : NaN;
      if (!Number.isFinite(server)) return;
      const offset = server - Date.now();
      // A few seconds is network delay and the header's whole seconds.
      const next = Math.abs(offset) < 5000 ? 0 : offset;
      if (next === clockOffset) return;
      clockOffset = next;
      sessionStorage.setItem(CLOCK_KEY, String(next));
    } catch (e) { /* storage blocked */ }
  }

  const shopSeconds = () => Math.floor((Date.now() + clockOffset) / 1000);

  /* ---------- Dates ---------- */

  const VAN_ICON = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" focusable="false"><path d="M2.5 6.5h11v9h-11z"/><path d="M13.5 9.5h4l3 3.2v2.8h-7"/><circle cx="6.5" cy="17" r="1.6"/><circle cx="17" cy="17" r="1.6"/></svg>';
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  function ordinal(n) {
    const mod100 = n % 100;
    const mod10 = n % 10;
    if (mod100 < 11 || mod100 > 13) {
      if (mod10 === 1) return `${n}st`;
      if (mod10 === 2) return `${n}nd`;
      if (mod10 === 3) return `${n}rd`;
    }
    return `${n}th`;
  }

  // "Thursday 8th October", or "Thursday 8th October 2026" with { year: true }.
  // Parsed at UTC noon and read with UTC getters, so the weekday never slips
  // for a viewer west of UTC (new Date('YYYY-MM-DD') is UTC midnight).
  function dayParts(iso) {
    if (typeof iso !== 'string' || !ISO_DATE.test(iso)) return null;
    const date = new Date(`${iso}T12:00:00Z`);
    if (isNaN(date.getTime())) return null;
    return {
      weekday: DAYS[date.getUTCDay()],
      day: `${ordinal(date.getUTCDate())} ${MONTHS[date.getUTCMonth()]}`,
      year: date.getUTCFullYear(),
    };
  }

  function formatDay(iso, options) {
    const parts = dayParts(iso);
    if (!parts) return '';
    return `${parts.weekday} ${parts.day}${options && options.year ? ` ${parts.year}` : ''}`;
  }

  // A hold's end as UK clock time ("14:32"), or blank once it has passed
  // (by the shop's clock) or when unknown, so a lapsed hold is never
  // quoted. MGP holds last about 15 minutes; the basket holds again when
  // it is opened after one lapses.
  function holdClock(seconds) {
    if (!Number.isFinite(seconds) || seconds <= shopSeconds()) return '';
    try {
      return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(seconds * 1000));
    } catch (e) {
      return '';
    }
  }

  /* ---------- Postcodes, SKUs, baskets ---------- */

  function origin() {
    const url = window.theme && theme.settings && theme.settings.pickADay;
    if (!url) return '';
    try {
      return new URL(url).origin;
    } catch (e) {
      return '';
    }
  }

  // Full postcodes only: the worker answers 400 to an outward code on its own.
  function fullPostcode(raw) {
    const compact = String(raw || '').replace(/\s+/g, '').toUpperCase();
    if (!UK_POSTCODE.test(compact)) return null;
    return { compact, display: `${compact.slice(0, -3)} ${compact.slice(-3)}` };
  }

  // Shared with the product page and Delivery page checkers (delivery-check.js).
  function recallPostcode() {
    try { return fullPostcode(localStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
  }

  function rememberPostcode(postcode) {
    try { localStorage.setItem(STORAGE_KEY, postcode.display); } catch (e) { /* storage blocked */ }
  }

  const validSku = (sku) => typeof sku === 'string' && SKU.test(sku.trim());

  // Merged by SKU and sorted, so the same basket always gives the same
  // signature. Blank and invalid SKUs are left out.
  function merge(lines) {
    const bySku = new Map();
    (Array.isArray(lines) ? lines : []).forEach((line) => {
      const sku = line && typeof line.sku === 'string' ? line.sku.trim() : '';
      if (!validSku(sku)) return;
      const quantity = Math.max(1, parseInt(line.quantity, 10) || 1);
      bySku.set(sku, (bySku.get(sku) || 0) + quantity);
    });
    return Array.from(bySku, ([sku, quantity]) => ({ sku, quantity }))
      .sort((a, b) => (a.sku < b.sku ? -1 : a.sku > b.sku ? 1 : 0));
  }

  const signature = (items) => items.map((item) => `${item.sku}:${item.quantity}`).join(',');

  // Beyond the worker's limits: the contact copy, and no call.
  const tooBig = (items) => items.length > MAX_ITEMS || items.some((item) => item.quantity > MAX_QUANTITY);

  /* ---------- Monitoring ---------- */

  // Only payload shape failures, once per page. Never 429s, 502s, network
  // errors or 409s: the Sentry key is capped at a few events an hour.
  const reported = new Set();
  // extra: optional details shown on the Sentry event; the message stays
  // the same so reports keep grouping under one issue.
  function report(what, extra) {
    if (reported.has(what)) return;
    reported.add(what);
    try {
      if (window.Sentry && typeof Sentry.captureMessage === 'function') {
        Sentry.captureMessage(`Waltons pick-a-day: ${what}`, extra ? { extra } : undefined);
      }
    } catch (e) { /* monitoring must never break the page */ }
  }

  // What came back instead of our JSON, so a report shows whether it was
  // an empty answer or a page put in its place (a work network's filter,
  // antivirus, an extension). The start is cut short and kept to plain
  // printable characters.
  function notJsonDetails(response, text) {
    let type = '';
    try { type = response.headers.get('Content-Type') || ''; } catch (e) { /* none */ }
    return {
      status: response.status,
      contentType: type.slice(0, 80),
      length: text.length,
      start: text.slice(0, 60).replace(/[^\x20-\x7E]+/g, ' ').trim(),
      redirected: !!response.redirected,
    };
  }

  /* ---------- Worker ---------- */

  const isIsoOrMissing = (value) => value === null || value === undefined || (typeof value === 'string' && ISO_DATE.test(value));
  const isNumberOrMissing = (value) => value === null || value === undefined || Number.isFinite(value);

  const VALID = {
    availability: (d) => !!d && typeof d.inStock === 'boolean' && typeof d.pickADay === 'boolean'
      && STATUSES.indexOf(d.status) > -1 && isIsoOrMissing(d.earliestDate),
    dates: (d) => VALID.availability(d) && Array.isArray(d.dates)
      && d.dates.every((day) => typeof day === 'string' && ISO_DATE.test(day))
      && (d.enqref === null || d.enqref === undefined || typeof d.enqref === 'string')
      && isNumberOrMissing(d.heldUntil),
    reserve: (d) => !!d && typeof d.enqref === 'string' && typeof d.date === 'string' && ISO_DATE.test(d.date)
      && isNumberOrMissing(d.heldUntil),
  };

  // Every outcome resolves: the parsed answer, { taken: true } for a reserve
  // 409, or { error: true } for anything else (4xx, 5xx, timeout, network,
  // a payload of the wrong shape).
  function post(route, body) {
    const base = origin();
    if (!base) return Promise.resolve({ error: true });
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), WORKER_TIMEOUT_MS) : null;
    let request;
    try {
      request = fetch(`${base}/mgp/${route}`, { method: 'POST', body: JSON.stringify(body), signal: ctrl ? ctrl.signal : undefined });
    } catch (e) {
      clearTimeout(timer);
      return Promise.resolve({ error: true });
    }
    return request
      .then((response) => {
        if (route === 'reserve' && response.status === 409) return { taken: true };
        if (!response.ok) return { error: true, status: response.status };
        // Read as text first so a body that is not JSON can be described.
        // A body cut off on the way (the timeout above, a dropped
        // connection, the page put away) rejects here with AbortError or
        // TypeError: a network error, not ours to report.
        return response.text().then((text) => {
          let data;
          try {
            data = JSON.parse(text);
          } catch (e) {
            report(`${route} answer was not JSON`, notJsonDetails(response, text));
            return { error: true };
          }
          if (VALID[route](data)) return data;
          report(`${route} answer failed validation`);
          return { error: true };
        }, () => ({ error: true }));
      })
      .catch(() => ({ error: true }))
      .then((result) => {
        clearTimeout(timer);
        return result;
      });
  }

  // Per page load. A failed answer is dropped at once, so a retry asks again.
  const memo = new Map();
  function remembered(key, ask, lifetime) {
    const hit = memo.get(key);
    if (hit && (hit.until === 0 || hit.until > now())) return hit.promise;
    const entry = { until: 0, promise: null };
    entry.promise = ask().then((result) => {
      const until = result && !result.error ? lifetime(result) : 0;
      if (until > now()) entry.until = until;
      else if (memo.get(key) === entry) memo.delete(key);
      return result;
    });
    memo.set(key, entry);
    return entry.promise;
  }

  function request(route, postcode, lines) {
    const pc = fullPostcode(postcode);
    const items = merge(lines);
    if (!pc || !items.length || tooBig(items) || !origin()) return null;
    return { pc, items, key: `${route}|${pc.display}|${signature(items)}` };
  }

  // Resolves to null when there is nothing to ask (no full postcode, no
  // valid SKU, too many lines).
  function availability(postcode, lines) {
    const req = request('availability', postcode, lines);
    if (!req) return Promise.resolve(null);
    return remembered(req.key, () => post('availability', { postcode: req.pc.display, items: req.items }), () => now() + AVAILABILITY_TTL_MS);
  }

  // Each call opens an MGP enquiry, so an answer is kept while its hold
  // lasts: re-renders and a second look never open another. The time left
  // is measured when the answer arrives and kept on the device's own
  // clock, so a clock that is off can neither expire an answer on arrival
  // (a new enquiry every time) nor keep one for hours.
  function dates(postcode, lines) {
    const req = request('dates', postcode, lines);
    if (!req) return Promise.resolve(null);
    return remembered(req.key, () => post('dates', { postcode: req.pc.display, items: req.items }), (result) => {
      if (!result.enqref) return now() + ANSWER_TTL_MS;
      const left = Number.isFinite(result.heldUntil) ? result.heldUntil - shopSeconds() : DEFAULT_HOLD_S;
      const span = left > 0 ? Math.min(left, MAX_HOLD_S) : DEFAULT_HOLD_S;
      return now() + (span - HOLD_MARGIN_S) * 1000;
    });
  }

  function forgetDates(postcode, lines) {
    const req = request('dates', postcode, lines);
    if (req) memo.delete(req.key);
  }

  function reserve(enqref, date) {
    if (typeof enqref !== 'string' || !enqref || typeof date !== 'string' || !ISO_DATE.test(date)) {
      return Promise.resolve({ error: true });
    }
    return post('reserve', { enqref, date }).then((result) => {
      if (result.taken || result.error) return result;
      return { ok: true, enqref: result.enqref, date: result.date, heldUntil: Number.isFinite(result.heldUntil) ? result.heldUntil : null };
    });
  }

  // One of the four answers from the worker, as a view for the step or
  // the basket. AVAILABLE with nothing to choose (no pick-a-day, no days
  // or no enquiry) is the team's to arrange, and is saved as such.
  function viewOf(result, key, pc, sig) {
    if (!result || result.error) return { key, kind: 'error', pc, sig };
    if (result.status === 'AVAILABLE' && result.pickADay && result.enqref && result.dates.length) {
      return { key, kind: 'days', pc, sig, result };
    }
    const status = result.status === 'AVAILABLE' ? 'CUSTOMER_CONTACT' : result.status;
    return { key, kind: 'contact', pc, sig, status };
  }

  // Whether the product page answer offers a day to choose.
  function productPick(answer) {
    return !!answer && !answer.error && answer.inStock !== false && answer.status === 'AVAILABLE' && answer.pickADay === true;
  }

  // The product page line under the postcode result. Blank hides it.
  // CUSTOMER_CONTACT has no line: Standard delivery reads "From" a day
  // instead (productContact, productFrom), when there is one.
  function productLine(answer) {
    if (!answer || answer.error || answer.inStock === false) return '';
    if (productPick(answer)) {
      const day = formatDay(answer.earliestDate);
      return `Choose your delivery day at checkout${day ? `, from ${day}` : ''}.`;
    }
    if (answer.status === 'DIRECT_DELIVERY') return 'Delivered direct. Our team will confirm your day after you order.';
    return '';
  }

  // Whether the product page answer leaves the day to MGP's team
  // (CUSTOMER_CONTACT), so Standard delivery reads "From" a day.
  function productContact(answer) {
    return !!answer && !answer.error && answer.inStock !== false && answer.status === 'CUSTOMER_CONTACT';
  }

  // Today in the UK, YYYY-MM-DD, by the shop's clock. Blank when the
  // browser cannot say.
  function ukToday() {
    try {
      const parts = {};
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(new Date(now() + clockOffset)).forEach((part) => { parts[part.type] = part.value; });
      const iso = `${parts.year}-${parts.month}-${parts.day}`;
      return ISO_DATE.test(iso) ? iso : '';
    } catch (e) {
      return '';
    }
  }

  // A worded date without a year, as the lead time line and the rates rows
  // print it ("17th October", or "Saturday 17th October"), as YYYY-MM-DD:
  // the year that puts it nearest today, so a stale date from last month
  // reads as past, and "5th January" in December as next year. Blank when
  // it is not a date.
  function isoFromWorded(worded, today) {
    const m = String(worded || '').match(/(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)/);
    const month = m ? MONTHS.findIndex((name) => name.toLowerCase() === m[2].toLowerCase()) : -1;
    if (month < 0 || !ISO_DATE.test(today)) return '';
    const day = Number(m[1]);
    const base = Date.parse(`${today}T12:00:00Z`);
    let best = '';
    let gap = Infinity;
    [-1, 0, 1].forEach((step) => {
      const date = new Date(Date.UTC(Number(today.slice(0, 4)) + step, month, day, 12));
      if (date.getUTCMonth() !== month || date.getUTCDate() !== day) return;
      const away = Math.abs(date.getTime() - base);
      if (away < gap) {
        gap = away;
        best = date.toISOString().slice(0, 10);
      }
    });
    return best;
  }

  // The day a CUSTOMER_CONTACT answer's Standard delivery reads "From",
  // worded ("Friday 16th October"): MGP's earliest day, or else the lead
  // time date the page already shows for the row (worded, no year), each
  // only when it is a real delivery day: two days or more after today, UK
  // time. MGP gives today's date when it has no day, and a lead time date
  // can be stale, so those are skipped. Blank when neither is real, and the
  // row stays as it is.
  function productFrom(answer, leadTime) {
    if (!productContact(answer)) return '';
    const today = ukToday();
    if (!today) return '';
    const soonest = new Date(`${today}T12:00:00Z`);
    soonest.setUTCDate(soonest.getUTCDate() + 2);
    const first = soonest.toISOString().slice(0, 10);
    const mgp = typeof answer.earliestDate === 'string' && ISO_DATE.test(answer.earliestDate) ? answer.earliestDate : '';
    if (mgp && mgp >= first) return formatDay(mgp);
    const lead = isoFromWorded(leadTime, today);
    return lead && lead >= first ? formatDay(lead) : '';
  }

  /* ---------- Cart attributes ---------- */

  // Our keys as the cart last reported them to this page, from the
  // /cart/update.js and /cart.js answers. attrsSeq counts the updates. A
  // section render can predate a save, and another tab (or checkout) can
  // change the cart after one, so when a render and these disagree the
  // basket asks the cart (PickADayBasket.syncAttrs).
  let serverAttrs = null;
  let attrsSeq = 0;
  let saving = 0;
  let queue = Promise.resolve(null);

  function ours(attributes) {
    const out = {};
    ALL_KEYS.forEach((key) => {
      const value = attributes && attributes[key];
      out[key] = value === null || value === undefined ? '' : String(value);
    });
    return out;
  }

  function setServerAttrs(attributes) {
    serverAttrs = ours(attributes);
    attrsSeq += 1;
  }

  const sameAttrs = (a, b) => ALL_KEYS.every((key) => ((a && a[key]) || '') === ((b && b[key]) || ''));

  const cartUrl = (route, fallback) => `${(window.theme && theme.routes && theme.routes[route]) || fallback}.js`;

  const blankKeys = (keys) => keys.reduce((out, key) => { out[key] = ''; return out; }, {});

  function dayAttributes(date, enqref, heldUntil, postcode, sig) {
    return {
      [KEYS.day]: formatDay(date, { year: true }),
      [KEYS.date]: date,
      [KEYS.enqref]: enqref,
      [KEYS.heldUntil]: String(heldUntil),
      [KEYS.postcode]: postcode,
      [KEYS.basket]: sig,
      [KEYS.status]: 'AVAILABLE',
    };
  }

  function contactAttributes(status, postcode, sig) {
    return Object.assign(blankKeys(DATE_KEYS), { [KEYS.status]: status || '', [KEYS.postcode]: postcode, [KEYS.basket]: sig });
  }

  // /cart/update.js merges attributes and '' deletes a key, so other apps'
  // keys are left alone. Writes go one at a time and resolve to the cart, or
  // null on failure. Headers are built here: the degraded theme.utils shim
  // in js-variables has no fetchConfig. cartUpdate is never published: that
  // would re-render the basket and move focus.
  function saveAttributes(attributes) {
    saving += 1;
    const run = () => {
      const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), SAVE_TIMEOUT_MS) : null;
      const done = (cart) => {
        clearTimeout(timer);
        saving -= 1;
        return cart;
      };
      try {
        return fetch(cartUrl('cart_update_url', '/cart/update'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
          signal: ctrl ? ctrl.signal : undefined,
        })
          .then((response) => {
            learnClock(response);
            return response.ok ? response.json() : null;
          })
          .then((cart) => {
            if (cart && cart.attributes) setServerAttrs(cart.attributes);
            return cart && cart.attributes ? cart : null;
          })
          .catch(() => null)
          .then(done);
      } catch (e) {
        return Promise.resolve(done(null));
      }
    };
    const job = queue.then(run, run);
    queue = job;
    return job;
  }

  const pending = () => queue;

  // Our keys as the cart has them now (GET /cart.js), or null on failure.
  // A save that lands meanwhile is newer, so it is not overwritten.
  function fetchAttrs() {
    const seq = attrsSeq;
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), SAVE_TIMEOUT_MS) : null;
    let request;
    try {
      request = fetch(cartUrl('cart_url', '/cart'), { headers: { Accept: 'application/json' }, signal: ctrl ? ctrl.signal : undefined });
    } catch (e) {
      clearTimeout(timer);
      return Promise.resolve(null);
    }
    return request
      .then((response) => {
        learnClock(response);
        return response.ok ? response.json() : null;
      })
      .then((cart) => {
        if (!cart || !cart.attributes) return null;
        if (seq === attrsSeq && saving < 1) setServerAttrs(cart.attributes);
        return Object.assign({}, serverAttrs);
      })
      .catch(() => null)
      .then((result) => {
        clearTimeout(timer);
        return result;
      });
  }

  // Our keys as this page knows them before a write: its last answer from
  // the cart, or the basket block's render. Null when it has neither.
  function knownAttrs() {
    if (serverAttrs) return Object.assign({}, serverAttrs);
    const basket = document.querySelector('pick-a-day-basket');
    return basket && typeof basket.liquidAttrs === 'function' ? basket.liquidAttrs() : null;
  }

  // Checkout waits, at most CHECKOUT_WAIT_MS, for a save still in flight and
  // for a day the shopper has just picked in the basket (still in its
  // debounce, holding with MGP, or saving), sending one still in the
  // debounce at once. Then it goes on regardless. Covers the drawer form
  // and form#cart (its button sits outside the form with form="cart").
  // Nothing else is gated.
  let resubmitting = false;
  // Once per visit, Checkout pressed with no day where one could be chosen
  // opens the day list first, with Continue to checkout straight on (see
  // PickADayBasket.askAtCheckout). Never a gate: Continue, a basket with no
  // pick-a-day, or a failed lookup all go on to checkout.
  const ASKED_KEY = 'waltons:pad-checkout-asked';
  let askedHere = false;
  const askedBefore = () => {
    if (askedHere) return true;
    try {
      return sessionStorage.getItem(ASKED_KEY) === '1';
    } catch (e) {
      return false;
    }
  };
  const markAsked = () => {
    askedHere = true;
    try { sessionStorage.setItem(ASKED_KEY, '1'); } catch (e) { /* storage blocked */ }
  };
  document.addEventListener('submit', (event) => {
    try {
      const submitter = event.submitter;
      if (resubmitting || !submitter || submitter.name !== 'checkout') return;
      if (!askedBefore()) {
        const form = event.target;
        // The block beside this checkout: the drawer's, or the cart page's.
        const place = form.closest('cart-drawer');
        const basket = Array.from(document.querySelectorAll('pick-a-day-basket'))
          .find((b) => b.closest('cart-drawer') === place && typeof b.wantsDay === 'function' && b.wantsDay());
        if (basket) {
          event.preventDefault();
          markAsked();
          // The theme's button marks itself busy on every submit, stopped or
          // not (HoverButton in theme.js), after this capture listener. Clear
          // it, or Checkout looks stuck while the day list is open.
          setTimeout(() => submitter.removeAttribute('aria-busy'), 0);
          basket.askAtCheckout(() => {
            if (!form.isConnected) return;
            if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
            else submitter.click();
          });
          return;
        }
      }
      const choices = Array.from(document.querySelectorAll('pick-a-day-basket'))
        .filter((basket) => typeof basket.choosing === 'function' && basket.choosing());
      if (saving < 1 && !choices.length) return;
      const form = event.target;
      event.preventDefault();
      const go = () => {
        resubmitting = true;
        try {
          if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
          else submitter.click();
        } finally {
          resubmitting = false;
        }
      };
      let work;
      try {
        work = Promise.all(choices.map((basket) => basket.flushChoice())).then(pending, pending);
      } catch (e) {
        work = pending();
      }
      Promise.race([work, wait(CHECKOUT_WAIT_MS)]).then(go, go);
    } catch (e) { /* never stand between a shopper and checkout */ }
  }, true);

  /* ---------- Shared picker markup (snippets/pick-a-day-picker.liquid) ---------- */

  function basketLines() {
    const blob = document.querySelector('script[data-pad-lines]');
    if (!blob) return [];
    try {
      const lines = JSON.parse(blob.textContent);
      return Array.isArray(lines) ? lines : [];
    } catch (e) {
      return [];
    }
  }

  function savedPostcode() {
    if (serverAttrs && serverAttrs[KEYS.postcode]) return fullPostcode(serverAttrs[KEYS.postcode]);
    const basket = document.querySelector('pick-a-day-basket');
    return basket ? fullPostcode(basket.getAttribute('data-postcode')) : null;
  }

  // Pickers quoting a hold end. Timers pause while a computer sleeps and
  // slow down in background tabs, so the time is also checked whenever the
  // page or the cart drawer comes back into view.
  const heldPickers = new Set();
  const refreshHeld = () => heldPickers.forEach((picker) => picker.refreshHeld());
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshHeld(); });
  window.addEventListener('pageshow', refreshHeld);
  window.addEventListener('focus', refreshHeld);
  document.addEventListener('drawer:afterShow', refreshHeld, true);

  class Picker {
    constructor(el, handlers) {
      this.el = el;
      this.handlers = handlers;
      this.input = el.querySelector('[data-pad-postcode]');
      this.go = el.querySelector('[data-pad-go]');
      this.status = el.querySelector('[data-pad-status]');
      this.retryButton = el.querySelector('[data-pad-retry]');
      this.days = el.querySelector('[data-pad-days]');
      this.list = el.querySelector('[data-pad-list]');
      this.prev = el.querySelector('[data-pad-prev]');
      this.next = el.querySelector('[data-pad-next]');
      this.monthLabel = el.querySelector('[data-pad-month]');
      this.name = (this.list && this.list.getAttribute('data-name')) || 'pad-day';
      this.months = [];
      this.monthIndex = 0;

      if (this.go) this.go.addEventListener('click', () => this.submit());
      if (this.input) {
        // Enter in the field, with no form around it (a nested form would
        // submit the cart page's form#cart).
        this.input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.submit();
        });
        this.input.addEventListener('input', () => this.input.removeAttribute('aria-invalid'));
      }
      if (this.retryButton) this.retryButton.addEventListener('click', () => handlers.retry());
      // aria-disabled, not disabled, at the ends: a disabled button that
      // has focus is blurred, dropping keyboard users to the page.
      const step = (button, by) => button.addEventListener('click', () => {
        if (button.getAttribute('aria-disabled') !== 'true') this.page(this.monthIndex + by);
      });
      if (this.prev) step(this.prev, -1);
      if (this.next) step(this.next, 1);
      if (this.list) {
        // Arrow keys move through the radios and tick each one on the way,
        // so a day reached with them is only held once the shopper settles
        // on it: Space or Enter on it, or focus leaving the list. A click or
        // tap holds after the short debounce.
        this.arrowedAt = 0;
        this.list.addEventListener('keydown', (event) => {
          if (ARROW_KEYS.indexOf(event.key) > -1) {
            this.arrowedAt = now();
            this.arrowAcross(event);
          } else if (event.key === 'Enter' || event.key === ' ') {
            // Enter would otherwise submit form#cart on the cart page.
            if (event.key === 'Enter') event.preventDefault();
            handlers.commit();
          }
        });
        // Each month is its own radio group (see monthGrid), so a day
        // ticked in one month unticks any other here.
        this.list.addEventListener('change', (event) => {
          const radio = event.target;
          if (!radio || radio.type !== 'radio' || !radio.checked) return;
          this.list.querySelectorAll('input[type=radio]:checked').forEach((other) => { if (other !== radio) other.checked = false; });
          handlers.choose(radio.value, now() - this.arrowedAt < ARROW_WINDOW_MS);
        });
        this.list.addEventListener('focusout', (event) => {
          if (!event.relatedTarget || !this.list.contains(event.relatedTarget)) handlers.commit();
        });
      }
    }

    submit() {
      const pc = fullPostcode(this.input && this.input.value);
      if (!pc) {
        this.say(COPY.badPostcode);
        if (this.input) {
          this.input.setAttribute('aria-invalid', 'true');
          this.input.focus();
        }
        return;
      }
      this.input.value = pc.display;
      rememberPostcode(pc);
      this.handlers.postcode(pc);
    }

    setPostcode(pc, onlyIfEmpty) {
      if (!this.input || !pc) return;
      if (onlyIfEmpty && this.input.value.trim()) return;
      this.input.value = pc.display;
    }

    focusInput(select) {
      if (!this.input) return;
      this.input.focus();
      if (select && typeof this.input.select === 'function') this.input.select();
    }

    say(text) {
      clearTimeout(this.heldTimer);
      this.heldShown = null;
      heldPickers.delete(this);
      if (this.status) this.status.textContent = text || '';
    }

    // COPY.held for a day, quoting MGP's hold end while it lasts and
    // dropping it once passed, so a lapsed time is never left on screen.
    // Any later message cancels the timer.
    sayHeld(iso, until) {
      const clock = holdClock(until);
      this.say(COPY.held(formatDay(iso), clock));
      if (!clock) return;
      this.heldShown = { iso, until };
      heldPickers.add(this);
      this.heldTimer = setTimeout(() => this.refreshHeld(), (until - shopSeconds()) * 1000 + 1000);
    }

    refreshHeld() {
      const shown = this.heldShown;
      if (!shown) return;
      if (!this.el.isConnected) {
        heldPickers.delete(this);
        return;
      }
      if (!holdClock(shown.until)) this.say(COPY.held(formatDay(shown.iso), ''));
    }

    retry(show) {
      if (this.retryButton) this.retryButton.hidden = !show;
    }

    hideDays() {
      if (!this.days) return;
      // A rebuild (a day just taken) takes focus with the old radios; the
      // next showDays puts it back if it comes soon after.
      if (this.days.contains(document.activeElement)) this.refocusAt = now();
      this.days.hidden = true;
      this.list.textContent = '';
      this.months = [];
    }

    // A month calendar, one month showing: every available day is a real
    // radio (arrows and Space work) named in full for screen readers; the
    // other days are shown greyed for orientation. All months are built,
    // in date order, so the radios keep the list's order; the month shown
    // is the chosen day's, else the month of near (a day just taken or
    // gone) or the next one with days, else the first.
    showDays(list, selected, near) {
      if (!this.days) return;
      this.list.textContent = '';
      const byMonth = new Map();
      list.filter((iso) => dayParts(iso)).sort().forEach((iso) => {
        const key = iso.slice(0, 7);
        if (!byMonth.has(key)) byMonth.set(key, new Set());
        byMonth.get(key).add(iso);
      });
      this.months = Array.from(byMonth.keys());
      this.months.forEach((key) => this.list.appendChild(this.monthGrid(key, byMonth.get(key), selected)));
      const want = (selected || near || '').slice(0, 7);
      let start = want ? this.months.findIndex((key) => key >= want) : 0;
      if (start < 0) start = this.months.length - 1;
      this.days.hidden = false;
      this.page(start);
      if (this.refocusAt && now() - this.refocusAt < REFOCUS_MS) {
        const grid = this.list.querySelectorAll('.pad-cal__grid')[this.monthIndex];
        const target = grid && (grid.querySelector('input[type=radio]:checked') || grid.querySelector('input[type=radio]'));
        if (target) target.focus();
      }
      this.refocusAt = 0;
    }

    monthGrid(key, available, selected) {
      const [year, month] = key.split('-').map(Number);
      const grid = document.createElement('div');
      grid.className = 'pad-cal__grid';
      grid.setAttribute('data-month', key);
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', `${MONTHS[month - 1]} ${year}`);
      // Monday first, as UK calendars are. UTC noon, as in dayParts.
      const lead = (new Date(Date.UTC(year, month - 1, 1, 12)).getUTCDay() + 6) % 7;
      const length = new Date(Date.UTC(year, month, 0, 12)).getUTCDate();
      for (let i = 0; i < lead; i += 1) {
        const blank = document.createElement('span');
        blank.className = 'pad-cal__blank';
        grid.appendChild(blank);
      }
      for (let d = 1; d <= length; d += 1) {
        const iso = `${key}-${d < 10 ? '0' : ''}${d}`;
        if (!available.has(iso)) {
          const off = document.createElement('span');
          off.className = 'pad-cal__off';
          off.setAttribute('aria-hidden', 'true');
          off.textContent = String(d);
          grid.appendChild(off);
          continue;
        }
        const label = document.createElement('label');
        label.className = 'pad-day';
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.className = 'pad-day__input';
        // One group per month: browsers let Tab into a group only at its
        // ticked radio, so a group spanning hidden months could leave the
        // month on screen unreachable.
        radio.name = `${this.name}-${key}`;
        radio.value = iso;
        radio.checked = iso === selected;
        const card = document.createElement('span');
        card.className = 'pad-day__card';
        const number = document.createElement('span');
        number.className = 'pad-day__num';
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(d);
        const name = document.createElement('span');
        name.className = 'sr-only';
        name.textContent = formatDay(iso);
        card.appendChild(number);
        card.appendChild(name);
        label.appendChild(radio);
        label.appendChild(card);
        grid.appendChild(label);
      }
      return grid;
    }

    page(index) {
      if (!this.months.length) return;
      this.monthIndex = Math.max(0, Math.min(index, this.months.length - 1));
      this.list.querySelectorAll('.pad-cal__grid').forEach((grid, i) => { grid.hidden = i !== this.monthIndex; });
      const [year, month] = this.months[this.monthIndex].split('-').map(Number);
      const label = `${MONTHS[month - 1]} ${year}`;
      // Written only when it changes, so the live label is not read again.
      if (this.monthLabel && this.monthLabel.textContent !== label) this.monthLabel.textContent = label;
      const single = this.months.length < 2;
      if (this.prev) {
        this.prev.hidden = single;
        this.prev.setAttribute('aria-disabled', String(this.monthIndex === 0));
      }
      if (this.next) {
        this.next.hidden = single;
        this.next.setAttribute('aria-disabled', String(this.monthIndex === this.months.length - 1));
      }
    }

    // Browsers skip radios in a hidden month, so an arrow past the first
    // or last day shown turns the page and ticks the next day there, as the
    // arrow would have within the month.
    arrowAcross(event) {
      const radio = event.target;
      if (!radio || radio.type !== 'radio' || event.altKey || event.ctrlKey || event.metaKey) return;
      const grid = radio.closest('.pad-cal__grid');
      if (!grid) return;
      const radios = Array.from(grid.querySelectorAll('input[type=radio]'));
      const forward = ['ArrowRight', 'ArrowDown', 'Right', 'Down'].indexOf(event.key) > -1;
      const edge = forward ? radios[radios.length - 1] : radios[0];
      const to = this.monthIndex + (forward ? 1 : -1);
      if (radio !== edge || to < 0 || to >= this.months.length) return;
      event.preventDefault();
      this.page(to);
      const shown = Array.from(this.list.querySelectorAll('.pad-cal__grid')[to].querySelectorAll('input[type=radio]'));
      const target = forward ? shown[0] : shown[shown.length - 1];
      if (!target) return;
      target.focus();
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clearChoice() {
      if (!this.list) return;
      this.list.querySelectorAll('input:checked').forEach((radio) => { radio.checked = false; });
    }

    holds(element) {
      return !!element && this.el.contains(element);
    }
  }

  /* ---------- Configurator Delivery day step ---------- */

  /*
    Mounted once per <product-configurator data-pad> (root.padStep). The
    wizard asks it enter() whenever the step is current, chosen() via done(),
    and the engine asks commitStep before /cart/add. The engine's own
    connectedCallback is not used: the drawer moves to <body> on every open.
    The step holds a day only while the postcode and the lines it was held
    for still match; any change asks again.
  */
  class DayStep {
    constructor(root, step) {
      this.root = root;
      this.step = step;
      this.pc = null;
      this.view = null;
      this.shown = null;
      this.held = null;
      this.loading = '';
      this.notice = '';
      this.asked = false;
      this.timer = null;
      // The day ticked but not yet sent: { view, date, seq }. seq is 0 for
      // one reached with the arrow keys until the shopper settles on it.
      this.choice = null;
      // The latest hold, chained on the one before so MGP gets them in the
      // order they were chosen. Only the reply to the latest choice
      // (holdSeq) counts.
      this.reserving = null;
      this.holdSeq = 0;
      // A day whose hold failed, kept ticked for Try again.
      this.retryDate = '';
      this.picker = new Picker(step, {
        postcode: (pc) => {
          if (!this.pc || this.pc.display !== pc.display) this.held = null;
          this.pc = pc;
          this.load(false);
        },
        choose: (date, byKeys) => this.choose(date, byKeys),
        commit: () => { if (this.choice) this.flush(); },
        retry: () => this.retry(),
      });

      root.addEventListener('change', (event) => this.onChange(event));
      root.addEventListener('drawer:afterShow', () => {
        if (!this.wizardMode() || this.isCurrent()) this.enter();
      });
      const reset = root.querySelector('[data-reset]');
      if (reset) reset.addEventListener('click', () => this.reset());
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
        theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, () => {
          try {
            this.syncVisibility();
            this.onChange();
          } catch (e) { /* a throwing subscriber would stop the others */ }
        });
      }
      this.syncVisibility();
    }

    wizardMode() {
      return this.root.classList.contains('is-wizard-ready');
    }

    isCurrent() {
      return this.step.classList.contains('is-wizard-current');
    }

    buildSku() {
      const variant = this.root.activeVariant;
      return variant && typeof variant.sku === 'string' ? variant.sku.trim() : '';
    }

    // A size with no valid SKU has nothing to ask MGP about: the step goes,
    // and the basket's check (or the team) covers it.
    syncVisibility() {
      const hide = !validSku(this.buildSku());
      if (this.step.hidden !== hide) {
        this.step.hidden = hide;
        this.refreshWizard();
      }
    }

    lines() {
      const build = typeof this.root.padLines === 'function'
        ? this.root.padLines()
        : [{ sku: this.buildSku(), quantity: 1 }];
      const items = merge(basketLines().concat(build));
      return { items, sig: signature(items), big: tooBig(items) };
    }

    keyFor(lines) {
      return this.pc ? `${this.pc.display}|${lines.sig}` : '';
    }

    refreshWizard() {
      if (this.root.wizard && typeof this.root.wizard.schedule === 'function') this.root.wizard.schedule();
    }

    // For the wizard's chosen(): a day held for exactly these lines, or an
    // answer that needs no choice (contact, error, nothing to ask). A hold
    // that has lapsed still counts: the basket holds the day again.
    done() {
      try {
        const lines = this.lines();
        if (!lines.items.length || lines.big) return true;
        const key = this.keyFor(lines);
        if (!key) return false;
        if (this.held && this.held.key === key) return true;
        return !!(this.view && this.view.key === key && this.view.kind !== 'days');
      } catch (e) {
        return false;
      }
    }

    // Called by the wizard on every render while this is the current step,
    // and on drawer open. Only asks MGP when the postcode or lines changed.
    enter() {
      if (!this.root.hasAttribute('open') || this.step.hidden) return;
      const lines = this.lines();
      if (!lines.items.length) return;
      if (!this.pc) this.pc = recallPostcode() || savedPostcode();
      if (!this.pc) {
        if (!this.asked) {
          this.asked = true;
          this.picker.say(COPY.needPostcode);
        }
        return;
      }
      this.picker.setPostcode(this.pc, true);
      this.load(true);
    }

    load(auto) {
      if (!this.pc) return;
      const lines = this.lines();
      if (!lines.items.length) return;
      const key = this.keyFor(lines);
      if (this.held && this.held.key !== key) {
        this.held = null;
        this.picker.clearChoice();
      }
      if (this.view && this.view.key === key && (auto || this.view.kind !== 'error')) {
        if (this.shown !== this.view) this.render();
        return;
      }
      if (this.loading === key) return;
      this.retryDate = '';
      if (lines.big) {
        this.view = { key, kind: 'contact', pc: this.pc, sig: lines.sig, status: '' };
        this.render();
        this.refreshWizard();
        return;
      }
      const pc = this.pc;
      this.loading = key;
      this.view = null;
      this.shown = null;
      this.picker.hideDays();
      this.picker.retry(false);
      this.picker.say(COPY.finding(pc.display));
      dates(pc.display, lines.items).then((result) => {
        if (this.loading !== key) return;
        this.loading = '';
        this.view = viewOf(result, key, pc, lines.sig);
        this.render();
        this.refreshWizard();
      });
    }

    render() {
      const view = this.view;
      this.shown = view;
      if (!view) return;
      if (view.kind === 'days') {
        const selected = this.held && this.held.key === view.key ? this.held.date : '';
        this.picker.showDays(view.result.dates, selected, this.near);
        this.near = '';
        this.picker.retry(false);
        if (selected) this.picker.sayHeld(selected, this.held.mgpUntil);
        else this.picker.say(this.notice || COPY.choose(view.pc.display));
        this.notice = '';
        return;
      }
      this.picker.hideDays();
      this.picker.retry(view.kind === 'error');
      this.picker.say(view.kind === 'error' ? COPY.stepError : COPY.contact);
    }

    // A click or tap holds after the debounce, with a new sequence number
    // at once so a slower reply to an earlier choice is ignored. A day
    // reached with the arrow keys waits for commit (see Picker).
    choose(date, byKeys) {
      const view = this.view;
      if (!view || view.kind !== 'days') return;
      clearTimeout(this.timer);
      this.timer = null;
      this.choice = { view, date, seq: byKeys ? 0 : ++this.holdSeq };
      if (byKeys) return;
      this.retryDate = '';
      this.picker.retry(false);
      this.picker.say(COPY.holding(formatDay(date)));
      this.timer = setTimeout(() => this.flush(), CHOOSE_DEBOUNCE_MS);
    }

    // Sends a choice still waiting on the debounce or the keyboard, and
    // resolves once the latest hold has settled.
    flush() {
      clearTimeout(this.timer);
      this.timer = null;
      const choice = this.choice;
      this.choice = null;
      if (choice && choice.view === this.view) {
        if (!choice.seq) {
          choice.seq = ++this.holdSeq;
          this.retryDate = '';
          this.picker.retry(false);
          this.picker.say(COPY.holding(formatDay(choice.date)));
        }
        const job = this.hold(choice.view, choice.date, choice.seq);
        const clear = () => {
          if (this.reserving === job) this.reserving = null;
        };
        this.reserving = job;
        job.then(clear, clear);
      }
      return this.reserving || Promise.resolve();
    }

    // Waits for the hold before it, so MGP sees the choices in order, and is
    // never sent once a later choice (or a change of lines) has replaced it.
    hold(view, date, seq) {
      const live = () => seq === this.holdSeq && this.view === view;
      const send = () => (live() ? reserve(view.result.enqref, date) : null);
      const before = this.reserving || Promise.resolve();
      return before.then(send, send).then((result) => {
        if (!result || !live()) return;
        if (result.ok) {
          // Only MGP's own hold end is ever quoted; the estimate is for
          // timing the basket's next hold.
          const mgpUntil = result.heldUntil || view.result.heldUntil || null;
          const heldUntil = mgpUntil || shopSeconds() + DEFAULT_HOLD_S;
          this.held = { key: view.key, date, enqref: result.enqref || view.result.enqref, heldUntil, mgpUntil, postcode: view.pc.display, sig: view.sig };
          this.retryDate = '';
          this.picker.retry(false);
          this.picker.sayHeld(date, mgpUntil);
        } else if (result.taken) {
          this.held = null;
          this.near = date;
          this.notice = COPY.taken;
          forgetDates(view.pc.display, this.lines().items);
          this.view = null;
          this.load(false);
        } else {
          // Kept ticked, with Try again: a busy moment (a 429) or a blip
          // should not undo the shopper's choice.
          this.held = null;
          this.retryDate = date;
          this.picker.say(COPY.stepHoldError);
          this.picker.retry(true);
        }
        this.refreshWizard();
      });
    }

    // Try again: the hold that failed, or the days that did not load.
    retry() {
      const view = this.view;
      if (this.retryDate && view && view.kind === 'days') {
        this.choose(this.retryDate, false);
        this.flush();
        return;
      }
      this.load(false);
    }

    onChange(event) {
      if (event && event.target && this.step.contains(event.target)) return;
      clearTimeout(this.changeTimer);
      this.changeTimer = setTimeout(() => this.recheck(), 80);
    }

    // An option ticked or a size changed: a day held for the old lines no
    // longer stands. The wizard re-enters when the step is current; the
    // flat drawer waits for Show days, so option clicks never open
    // enquiries of their own. Only a shopper who had picked a day is told
    // it went; otherwise the list just goes.
    recheck() {
      if (!this.pc) return;
      const key = this.keyFor(this.lines());
      const stale = (this.held && this.held.key !== key)
        || (this.view && this.view.key !== key)
        || (this.loading && this.loading !== key);
      if (!stale) return;
      const hadDay = !!(this.held || this.choice || this.reserving || this.retryDate);
      this.held = null;
      this.view = null;
      this.shown = null;
      this.loading = '';
      clearTimeout(this.timer);
      this.timer = null;
      this.choice = null;
      this.retryDate = '';
      this.holdSeq += 1;
      this.picker.clearChoice();
      this.picker.hideDays();
      this.picker.retry(false);
      const wizard = this.wizardMode();
      this.notice = hadDay ? COPY.changed : '';
      if (hadDay) this.picker.say(wizard ? COPY.changed : COPY.changedFlat);
      else this.picker.say(wizard ? '' : COPY.showDays);
      if (wizard && this.isCurrent()) this.enter();
      this.refreshWizard();
    }

    reset() {
      clearTimeout(this.timer);
      this.timer = null;
      this.choice = null;
      this.holdSeq += 1;
      this.held = null;
      this.view = null;
      this.shown = null;
      this.loading = '';
      this.notice = '';
      this.retryDate = '';
      this.picker.hideDays();
      this.picker.retry(false);
      this.picker.say('');
    }

    // What commitStep saves: the held day, or a no-choice answer, for
    // exactly the lines about to be in the basket. Anything else saves
    // nothing and the basket asks later. A held day is saved even when its
    // hold has lapsed by this device's clock: the basket holds the same day
    // again when it opens (case D), or asks again.
    attributes() {
      const lines = this.lines();
      const key = this.keyFor(lines);
      if (!key || !lines.items.length || lines.big) return null;
      const held = this.held;
      if (held && held.key === key) {
        return dayAttributes(held.date, held.enqref, held.heldUntil, held.postcode, held.sig);
      }
      const view = this.view;
      if (view && view.key === key && view.kind === 'contact' && view.status) {
        return contactAttributes(view.status, view.pc.display, view.sig);
      }
      return null;
    }
  }

  // Before /cart/add, so the add's own drawer render already carries the
  // day. Capped and never rejects: the day is never a gate on adding.
  // Resolves to an undo when it saved something (see addToCart): a failed
  // add must not leave a day on the cart for a basket it never reached.
  function commitStep(root) {
    const step = root && root.padStep;
    if (!step) return Promise.resolve();
    let undo;
    let work;
    try {
      work = step.flush()
        .then(() => {
          const attributes = step.attributes();
          if (!attributes) return null;
          const before = knownAttrs();
          // Queued behind the save, so it always lands after it.
          undo = () => saveAttributes(before ? ours(before) : blankKeys(ALL_KEYS));
          return saveAttributes(attributes);
        })
        .catch(() => null);
    } catch (e) {
      return Promise.resolve();
    }
    return Promise.race([work, wait(COMMIT_WAIT_MS)]).then(() => undo, () => undo);
  }

  function mountSteps() {
    document.querySelectorAll('product-configurator[data-pad]').forEach((root) => {
      if (root.padStep) return;
      const step = root.querySelector('[data-day-step]');
      if (!step) return;
      try {
        root.padStep = new DayStep(root, step);
      } catch (e) { /* the configurator works without the step */ }
    });
  }

  // This file is deferred from <head>, so it runs before the body scripts
  // that define product-configurator. Mount once they have: at
  // DOMContentLoaded, or at load as a fallback. Mounting is idempotent.
  if (document.readyState === 'complete') {
    mountSteps();
  } else {
    document.addEventListener('DOMContentLoaded', mountSteps);
    window.addEventListener('load', mountSteps);
  }

  /* ---------- Basket (cart drawer and /cart) ---------- */

  function parseAttrs(attrs) {
    return {
      date: attrs[KEYS.date] || '',
      enqref: attrs[KEYS.enqref] || '',
      heldUntil: parseInt(attrs[KEYS.heldUntil], 10) || 0,
      postcode: attrs[KEYS.postcode] || '',
      basket: attrs[KEYS.basket] || '',
      status: attrs[KEYS.status] || '',
      any: ALL_KEYS.some((key) => attrs[key]),
    };
  }

  /*
    Rebuilt from Liquid on every cart change (the drawer and cart sections
    are re-rendered wholesale), so each instance decides afresh:
      A  no lines to send: clear our keys, show nothing
      B  saved day, same lines and postcode, hold good for 2+ minutes: show it
      C  saved no-choice answer for the same lines: the contact copy, with
         the postcode it was for and Change postcode
      D  saved day, but the lines changed or the hold is near its end:
         check again, only once the basket is open
      E  nothing saved: offer the picker; MGP is asked only when the shopper
         opens it
    "Open" is /cart, or the drawer with [open]; background re-renders (an
    add from the product page) never open an MGP enquiry.
  */
  class PickADayBasket extends HTMLElement {
    // The drawer moves itself to <body> when it opens and back when it
    // closes, which disconnects and reconnects this element. That is not a
    // re-render: the element carries on as it was.
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      try {
        this.setup();
      } catch (e) {
        this.hidden = true;
      }
    }

    // A move reconnects before this microtask runs; a re-render does not.
    disconnectedCallback() {
      Promise.resolve().then(() => {
        if (!this.isConnected) this.teardown();
      });
    }

    teardown() {
      clearTimeout(this.timer);
      clearTimeout(this.chooseTimer);
      clearTimeout(this.sayTimer);
      if (this.drawer && this.onShow) this.drawer.removeEventListener('drawer:afterShow', this.onShow);
      if (this.drawer && this.onSync) this.drawer.removeEventListener('drawer:afterShow', this.onSync);
      if (this.drawer && this.onHide) this.drawer.removeEventListener('drawer:afterHide', this.onHide);
      this.onShow = null;
      this.onSync = null;
      this.onHide = null;
      this.nextChoice = null;
      this.holding = null;
      this.proceed = null;
      this.run = (this.run || 0) + 1;
    }

    setup() {
      const blob = this.querySelector('script[data-pad-lines]');
      let lines = [];
      try {
        lines = JSON.parse(blob ? blob.textContent : '[]');
      } catch (e) {
        lines = [];
      }
      if (!Array.isArray(lines)) lines = [];
      const items = merge(lines);
      this.model = {
        items,
        sig: signature(items),
        eligible: items.length > 0,
        big: tooBig(items),
        mixed: lines.some((line) => line && line.ship && !validSku(line.sku)),
      };
      this.run = 0;
      this.pickerOpen = false;
      this.view = null;
      this.notice = '';
      this.retryDate = '';
      // A day ticked but not yet sent ({ view, date, token }; token 0 for one
      // reached with the arrow keys), and the hold (then save) in progress.
      this.nextChoice = null;
      this.holding = null;
      this.seen = -1;
      this.drawer = this.closest('cart-drawer');
      this.els = {
        saved: this.querySelector('[data-pad-saved]'),
        savedDay: this.querySelector('[data-pad-saved-day]'),
        change: this.querySelector('[data-pad-change]'),
        contact: this.querySelector('[data-pad-contact]'),
        where: this.querySelector('[data-pad-where]'),
        wherePostcode: this.querySelector('[data-pad-where-postcode]'),
        repostcode: this.querySelector('[data-pad-repostcode]'),
        open: this.querySelector('[data-pad-open]'),
        ui: this.querySelector('[data-pad-ui]'),
        mixed: this.querySelector('[data-pad-mixed]'),
        say: this.querySelector('[data-pad-say]'),
        ask: this.querySelector('[data-pad-ask]'),
        leaf: this.querySelector('[data-pad-leaf]'),
        continue: this.querySelector('[data-pad-continue]'),
      };
      this.proceed = null;
      this.picker = new Picker(this.els.ui, {
        postcode: (pc) => this.onPostcode(pc),
        choose: (date, byKeys) => this.choose(date, byKeys),
        commit: () => { if (this.nextChoice) this.flushChoice(); },
        retry: () => this.retry(),
      });
      this.els.open.addEventListener('click', () => this.openPicker(true));
      this.els.change.addEventListener('click', () => this.openPicker(false));
      if (this.els.repostcode) this.els.repostcode.addEventListener('click', () => this.changePostcode());
      if (this.els.continue) this.els.continue.addEventListener('click', () => this.goOn());
      this.attrs = this.liquidAttrs();
      // Wait for a save from a previous instance, settle which copy of our
      // keys to trust, then decide.
      pending()
        .then(() => this.syncAttrs())
        .then(() => {
          if (this.isConnected) this.evaluate();
        });
      // A save made elsewhere on this page with no re-render after it (the
      // configurator's Delivery day step, or its undo after a failed add):
      // look again when the drawer next opens.
      if (this.drawer) {
        this.onSync = () => {
          if (!serverAttrs || this.seen === attrsSeq || saving > 0) return;
          this.adopt(serverAttrs);
        };
        this.drawer.addEventListener('drawer:afterShow', this.onSync);
        // Closing the drawer while asked at checkout drops the ask: checkout
        // never opens by itself later.
        this.onHide = () => { if (this.proceed) this.goOn(true); };
        this.drawer.addEventListener('drawer:afterHide', this.onHide);
      }
    }

    liquidAttrs() {
      const out = {};
      out[KEYS.day] = this.getAttribute('data-day') || '';
      out[KEYS.date] = this.getAttribute('data-date') || '';
      out[KEYS.enqref] = this.getAttribute('data-enqref') || '';
      out[KEYS.heldUntil] = this.getAttribute('data-held-until') || '';
      out[KEYS.postcode] = this.getAttribute('data-postcode') || '';
      out[KEYS.basket] = this.getAttribute('data-basket') || '';
      out[KEYS.status] = this.getAttribute('data-status') || '';
      return out;
    }

    // Which copy of our keys to trust. The render and this page's last word
    // from the cart agree, or the page has none: the render. They differ
    // (the render predates a save, or another tab or checkout changed the
    // cart since this page last heard): ask the cart, and fall back to the
    // page's copy if that fails.
    syncAttrs() {
      const liquid = this.liquidAttrs();
      if (!serverAttrs || sameAttrs(serverAttrs, liquid)) {
        this.attrs = liquid;
        this.seen = attrsSeq;
        return Promise.resolve();
      }
      return fetchAttrs().then((fresh) => {
        this.attrs = Object.assign({}, fresh || serverAttrs);
        this.seen = attrsSeq;
      });
    }

    // Takes a newer copy of our keys and decides again, when it differs.
    adopt(attrs) {
      this.seen = attrsSeq;
      if (!attrs || sameAttrs(attrs, this.attrs)) return;
      this.attrs = Object.assign({}, attrs);
      this.evaluate();
    }

    write(attributes) {
      return saveAttributes(attributes).then((cart) => {
        if (cart) {
          this.attrs = serverAttrs ? Object.assign({}, serverAttrs) : Object.assign({}, this.attrs, attributes);
          this.seen = attrsSeq;
        }
        return cart;
      });
    }

    openContext() {
      if (window.theme && theme.settings && theme.settings.isCartTemplate) return true;
      return !!(this.drawer && this.drawer.hasAttribute('open'));
    }

    // Runs fn once the basket is open (now, or on the drawer's next open),
    // debounced so quantity clicks settle first.
    whenOpen(fn) {
      const go = () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => { if (this.isConnected) fn(); }, OPEN_DEBOUNCE_MS);
      };
      if (this.openContext()) {
        go();
        return;
      }
      const drawer = this.drawer;
      if (!drawer) return;
      if (this.onShow) drawer.removeEventListener('drawer:afterShow', this.onShow);
      this.onShow = () => {
        drawer.removeEventListener('drawer:afterShow', this.onShow);
        this.onShow = null;
        go();
      };
      drawer.addEventListener('drawer:afterShow', this.onShow);
    }

    evaluate() {
      const model = this.model;
      const saved = parseAttrs(this.attrs);
      if (this.els.mixed) this.els.mixed.hidden = !(model.mixed && model.eligible);

      if (!model.eligible) {
        this.hidden = true;
        this.state('none');
        if (saved.any) this.write(blankKeys(ALL_KEYS));
        return;
      }
      this.hidden = false;

      if (model.big) {
        if (saved.any) this.write(blankKeys(ALL_KEYS));
        this.state('contact');
        return;
      }

      const pc = fullPostcode(saved.postcode);
      const fresh = !!pc && saved.basket === model.sig;

      if (saved.date) {
        if (!ISO_DATE.test(saved.date) || !saved.enqref || !pc) {
          this.write(blankKeys(DAY_KEYS));
          this.state('prompt');
          return;
        }
        this.state('saved');
        if (fresh && saved.heldUntil - shopSeconds() > HOLD_MARGIN_S) return;
        this.whenOpen(() => this.keep());
        return;
      }

      if (fresh && CONTACT_STATUSES.indexOf(saved.status) > -1) {
        this.state('contact');
        return;
      }

      // A no-choice answer or signature left from an earlier basket, or a
      // status with no day behind it (AVAILABLE is only saved with a day).
      if (!fresh && (saved.status || saved.basket)) this.write({ [KEYS.status]: '', [KEYS.basket]: '' });
      else if (saved.status) this.write({ [KEYS.status]: '' });
      this.state('prompt');
    }

    // Case D: the lines changed, or the hold is ending. Keep the same day on
    // a new enquiry when MGP still offers it; otherwise ask again. A failed
    // lookup keeps the saved day and writes nothing. A shopper already in
    // the picker is choosing for themselves, so it stands aside.
    keep() {
      const model = this.model;
      const saved = parseAttrs(this.attrs);
      const pc = fullPostcode(saved.postcode);
      if (!saved.date || !pc || this.pickerOpen || this.choosing()) return;
      const changed = saved.basket !== model.sig;
      const token = ++this.run;
      this.setAttribute('aria-busy', 'true');
      const settle = () => this.removeAttribute('aria-busy');
      dates(pc.display, model.items).then((result) => {
        if (token !== this.run) return null;
        const view = viewOf(result, `${pc.display}|${model.sig}`, pc, model.sig);
        if (view.kind === 'error') return null;
        if (view.kind === 'contact') {
          const attributes = contactAttributes(view.status, pc.display, model.sig);
          this.write(attributes);
          this.attrs = Object.assign({}, this.attrs, attributes);
          settle();
          this.state('contact');
          this.announce(COPY.contact);
          return null;
        }
        if (view.result.dates.indexOf(saved.date) === -1) {
          this.ask(view, changed, saved.date);
          return null;
        }
        return reserve(view.result.enqref, saved.date).then((held) => {
          if (token !== this.run) return null;
          if (held.ok) {
            const heldUntil = held.heldUntil || view.result.heldUntil || shopSeconds() + DEFAULT_HOLD_S;
            return this.write(dayAttributes(saved.date, held.enqref || view.result.enqref, heldUntil, pc.display, model.sig));
          }
          if (held.taken) {
            forgetDates(pc.display, model.items);
            this.ask(view, changed, saved.date);
          }
          return null;
        });
      }).then(settle, settle);
    }

    // The saved day has gone: clear it and show the days left. The text is
    // written before the picker is shown and then announced separately, as
    // a live region revealed and filled in the same tick is often not read
    // out (nor is one inside an aria-busy element).
    ask(view, changed, gone) {
      const clear = blankKeys(DAY_KEYS);
      this.write(clear);
      this.attrs = Object.assign({}, this.attrs, clear);
      this.removeAttribute('aria-busy');
      this.view = view;
      this.pc = view.pc;
      const left = view.result.dates.filter((day) => day !== gone);
      const text = left.length ? (changed ? COPY.basketChanged : COPY.dayGone) : COPY.contact;
      this.picker.setPostcode(view.pc);
      this.picker.retry(false);
      if (left.length) this.picker.showDays(left, '', gone);
      else this.picker.hideDays();
      this.picker.say(text);
      this.state('prompt');
      this.showPicker();
      this.announce(text);
    }

    // The block's own always-rendered live region (visually hidden, outside
    // anything that is shown and hidden), written just after the change it
    // describes.
    announce(text) {
      const say = this.els.say;
      if (!say) return;
      clearTimeout(this.sayTimer);
      say.textContent = '';
      this.sayTimer = setTimeout(() => {
        if (this.isConnected) say.textContent = text;
      }, ANNOUNCE_DELAY_MS);
    }

    showPicker() {
      this.pickerOpen = true;
      this.els.ui.hidden = false;
      this.els.open.hidden = true;
    }

    openPicker(moveFocus) {
      this.showPicker();
      const saved = parseAttrs(this.attrs);
      const pc = fullPostcode(saved.postcode) || recallPostcode();
      if (pc) {
        this.picker.setPostcode(pc);
        this.load(pc);
        if (moveFocus) this.els.ui.focus({ preventScroll: true });
      } else {
        this.picker.say(COPY.needPostcode);
        if (moveFocus) this.picker.focusInput();
      }
    }

    // Change postcode from the contact copy: the picker with the postcode
    // field ready to edit. Nothing is asked until a postcode is sent.
    changePostcode() {
      const saved = parseAttrs(this.attrs);
      const pc = fullPostcode(saved.postcode) || this.pc;
      this.run += 1;
      this.dropChoice();
      this.view = null;
      this.showPicker();
      this.state('prompt');
      this.picker.hideDays();
      this.picker.retry(false);
      if (pc) this.picker.setPostcode(pc);
      this.picker.say(COPY.needPostcode);
      this.picker.focusInput(true);
    }

    // For the checkout ask: a day could be chosen here and none has been.
    wantsDay() {
      return this.isConnected && !this.hidden && this.kind === 'prompt' && !this.pickerOpen
        && !this.choosing() && !!this.model && this.model.eligible && !this.model.big;
    }

    // Checkout pressed with no day: open the day list with the ask line and
    // Continue to checkout. proceed submits the checkout as pressed.
    askAtCheckout(proceed) {
      this.proceed = proceed;
      if (this.els.ask) this.els.ask.hidden = false;
      if (this.els.continue) this.els.continue.hidden = false;
      this.reveal();
      if (this.els.ask) this.announce(this.els.ask.textContent);
    }

    // Continue to checkout, or on by itself when there turns out to be no
    // day to choose (a basket MGP cannot give a day for, or a failed
    // lookup), as long as the basket is still open.
    goOn(auto) {
      const proceed = this.proceed;
      this.proceed = null;
      if (this.els.ask) this.els.ask.hidden = true;
      if (this.els.continue) this.els.continue.hidden = true;
      if (proceed && (!auto || this.openContext())) proceed();
    }

    // Scroll to the block and open the day list (the drawer footer's button).
    reveal() {
      this.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      if (!this.pickerOpen && (this.kind === 'prompt' || this.kind === 'saved')) {
        this.openPicker(true);
      } else {
        this.els.ui.focus({ preventScroll: true });
      }
    }

    load(pc) {
      const model = this.model;
      this.pc = pc;
      const token = ++this.run;
      this.view = null;
      this.retryDate = '';
      this.dropChoice();
      this.picker.hideDays();
      this.picker.retry(false);
      this.picker.say(COPY.finding(pc.display));
      dates(pc.display, model.items).then((result) => {
        if (token !== this.run || !this.isConnected) return;
        const view = viewOf(result, `${pc.display}|${model.sig}`, pc, model.sig);
        this.view = view;
        if (view.kind === 'error') {
          this.picker.say(COPY.basketError);
          this.picker.retry(true);
          if (this.proceed) this.goOn(true);
          return;
        }
        if (view.kind === 'contact') {
          const hadFocus = this.picker.holds(document.activeElement);
          const attributes = contactAttributes(view.status, pc.display, model.sig);
          const saving = this.write(attributes);
          this.attrs = Object.assign({}, this.attrs, attributes);
          this.state('contact');
          // Focus on the copy reads it out; otherwise announce it.
          if (hadFocus && this.els.contact) this.els.contact.focus({ preventScroll: true });
          else this.announce(COPY.contact);
          // On to checkout once the no-choice answer is saved with the cart.
          if (this.proceed) saving.then(() => { if (this.proceed) this.goOn(true); }, () => { if (this.proceed) this.goOn(true); });
          return;
        }
        const saved = parseAttrs(this.attrs);
        const selected = saved.date && saved.basket === model.sig && saved.postcode === pc.display ? saved.date : '';
        const ticked = view.result.dates.indexOf(selected) > -1 ? selected : '';
        this.picker.showDays(view.result.dates, ticked, this.near);
        this.near = '';
        if (ticked && !this.notice) this.picker.sayHeld(ticked, saved.heldUntil);
        else this.picker.say(this.notice || COPY.choose(pc.display));
        this.notice = '';
      });
    }

    onPostcode(pc) {
      const saved = parseAttrs(this.attrs);
      // A new postcode voids a day, or a no-choice answer, saved for the old one.
      if ((saved.date || saved.status) && saved.postcode !== pc.display) {
        const clear = blankKeys(DAY_KEYS);
        this.write(clear);
        this.attrs = Object.assign({}, this.attrs, clear);
        this.state('prompt');
      }
      this.load(pc);
    }

    dropChoice() {
      clearTimeout(this.chooseTimer);
      this.nextChoice = null;
    }

    // A click or tap holds after the debounce, and at once makes any reply
    // still due for an earlier choice (or a re-check) stale. A day reached
    // with the arrow keys waits for commit (see Picker).
    choose(date, byKeys) {
      const view = this.view;
      if (!view || view.kind !== 'days') return;
      clearTimeout(this.chooseTimer);
      this.nextChoice = { view, date, token: byKeys ? 0 : ++this.run };
      if (byKeys) return;
      this.retryDate = '';
      this.picker.retry(false);
      this.picker.say(COPY.holding(formatDay(date)));
      this.chooseTimer = setTimeout(() => this.flushChoice(), CHOOSE_DEBOUNCE_MS);
    }

    // For the checkout guard: a day on its way to the cart.
    choosing() {
      return !!(this.nextChoice || this.holding);
    }

    // Sends a choice still waiting on the debounce or the keyboard now, and
    // resolves once the latest hold, and its save, have settled.
    flushChoice() {
      clearTimeout(this.chooseTimer);
      const next = this.nextChoice;
      this.nextChoice = null;
      if (next && next.view === this.view) {
        if (!next.token) {
          next.token = ++this.run;
          this.retryDate = '';
          this.picker.retry(false);
          this.picker.say(COPY.holding(formatDay(next.date)));
        }
        const job = this.hold(next.view, next.date, next.token);
        const clear = () => {
          if (this.holding === job) this.holding = null;
        };
        this.holding = job;
        job.then(clear, clear);
      }
      return this.holding || Promise.resolve();
    }

    // Waits for the hold (and save) before it, so MGP and the cart see the
    // choices in order, and is never sent once a later choice has replaced it.
    hold(view, date, token) {
      const live = () => token === this.run && this.view === view && this.isConnected;
      const send = () => (live() ? reserve(view.result.enqref, date) : null);
      const before = this.holding || Promise.resolve();
      return before.then(send, send).then((held) => {
        if (!held || !live()) return null;
        if (held.taken) {
          this.notice = COPY.taken;
          this.near = date;
          forgetDates(view.pc.display, this.model.items);
          this.load(view.pc);
          return null;
        }
        if (!held.ok) {
          // Kept ticked, with Try again: a busy moment (a 429) or a blip
          // should not undo the shopper's choice.
          this.retryDate = date;
          this.picker.say(COPY.holdError);
          this.picker.retry(true);
          return null;
        }
        const mgpUntil = held.heldUntil || view.result.heldUntil || null;
        const heldUntil = mgpUntil || shopSeconds() + DEFAULT_HOLD_S;
        return this.write(dayAttributes(date, held.enqref || view.result.enqref, heldUntil, view.pc.display, view.sig)).then((cart) => {
          if (!live()) return;
          if (!cart) {
            this.picker.clearChoice();
            this.picker.say(COPY.saveError);
            return;
          }
          this.state('saved');
          this.picker.sayHeld(date, mgpUntil);
        });
      });
    }

    // Try again: the hold that failed, or the days that did not load.
    retry() {
      const view = this.view;
      if (this.retryDate && view && view.kind === 'days') {
        this.choose(this.retryDate, false);
        this.flushChoice();
        return;
      }
      if (this.pc) this.load(this.pc);
    }

    // Shows one of: the saved day, the contact copy, the "choose" button,
    // or nothing, and mirrors it in the drawer footer.
    state(kind) {
      this.kind = kind;
      if (kind === 'saved' && this.els.ask) this.els.ask.hidden = true;
      const saved = parseAttrs(this.attrs);
      this.syncLeaf(kind === 'saved' ? saved.date : '');
      const day = kind === 'saved' ? formatDay(saved.date) : '';
      this.els.saved.hidden = kind !== 'saved';
      if (day && this.els.savedDay) this.els.savedDay.textContent = day;
      this.els.contact.hidden = kind !== 'contact';
      if (this.els.where) {
        // The postcode the answer was for, with Change postcode: a mistyped
        // or remembered postcode must not lock the basket into this copy.
        const pc = kind === 'contact' && !this.model.big ? fullPostcode(saved.postcode) : null;
        this.els.where.hidden = !pc;
        if (pc && this.els.wherePostcode) this.els.wherePostcode.textContent = pc.display;
      }
      this.els.open.hidden = kind !== 'prompt' || this.pickerOpen;
      if (kind === 'contact' || kind === 'none') {
        this.els.ui.hidden = true;
        this.pickerOpen = false;
      }
      this.syncFooter(kind, day);
    }

    // The card's calendar leaf: month over day for a saved day, else a van.
    syncLeaf(iso) {
      const leaf = this.els.leaf;
      const date = ISO_DATE.test(iso || '') ? iso : '';
      if (!leaf || leaf.getAttribute('data-for') === date) return;
      leaf.setAttribute('data-for', date);
      leaf.classList.toggle('is-set', !!date);
      if (date) {
        const d = new Date(`${date}T12:00:00Z`);
        leaf.innerHTML = `<span class="pad-basket__leaf-m">${MONTHS[d.getUTCMonth()].slice(0, 3)}</span><span class="pad-basket__leaf-d">${d.getUTCDate()}</span>`;
      } else {
        leaf.innerHTML = VAN_ICON;
      }
    }

    syncFooter(kind, day) {
      const host = this.closest('[id^="MiniCart-"]');
      const foot = host && host.querySelector('[data-pad-foot]');
      if (!foot) return;
      const label = foot.querySelector('[data-pad-foot-label]');
      const value = foot.querySelector('[data-pad-foot-day]');
      const go = foot.querySelector('[data-pad-foot-go]');
      if (kind !== 'saved' && kind !== 'prompt') {
        foot.hidden = true;
        return;
      }
      foot.hidden = false;
      if (label) label.hidden = kind !== 'saved';
      if (value) value.textContent = day;
      if (go) {
        go.textContent = kind === 'saved' ? COPY.footerChange : COPY.footerChoose;
        if (kind === 'saved') go.setAttribute('aria-label', COPY.footerChangeLabel);
        else go.removeAttribute('aria-label');
      }
    }
  }

  if (!customElements.get('pick-a-day-basket')) customElements.define('pick-a-day-basket', PickADayBasket);

  // The drawer footer's line: its button takes the shopper to the block.
  document.addEventListener('click', (event) => {
    const go = event.target && event.target.closest ? event.target.closest('[data-pad-foot-go]') : null;
    if (!go) return;
    const host = go.closest('[id^="MiniCart-"]') || document;
    const basket = host.querySelector('pick-a-day-basket');
    if (!basket || typeof basket.reveal !== 'function' || basket.hidden) return;
    event.preventDefault();
    basket.reveal();
  });

  // Restored from the back/forward cache: the cart may have changed while
  // the page was away (another tab, checkout), and nothing re-renders it.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    try {
      const baskets = Array.from(document.querySelectorAll('pick-a-day-basket'));
      if (!baskets.length) return;
      fetchAttrs().then((fresh) => {
        if (!fresh) return;
        baskets.forEach((basket) => {
          if (basket.isConnected && typeof basket.adopt === 'function') basket.adopt(fresh);
        });
      });
    } catch (e) { /* the basket keeps what it shows */ }
  });

  window.WaltonsPickADay = {
    keys: KEYS,
    origin,
    fullPostcode,
    recallPostcode,
    rememberPostcode,
    validSku,
    merge,
    signature,
    availability,
    dates,
    reserve,
    formatDay,
    productLine,
    productPick,
    productContact,
    productFrom,
    saveAttributes,
    pending,
    commitStep,
  };
})();
